import React from 'react';
import { motion } from 'framer-motion';
import { Leaf } from 'lucide-react';

// NOTE: Update this path to match your project structure
import NutritionVisual from '@/res/imgs/nutrition_alpha.webp';

// --- STRICT TYPING --- //
export interface NutritionHeroHeaderProps {
  title?: string;
  description?: string | React.ReactNode;
  className?: string;
  isCurved?: boolean;
  /** Optional children to inject filters, search bars, or extra data below the text */
  children?: React.ReactNode;
}

// --- CURVE DIVIDER --- //
const CurveDivider = ({ className = '' }: { className?: string }) => (
  <div
    id="curved-divider"
    className={`absolute bottom-0 left-0 w-full z-0 h-24 sm:h-32 overflow-hidden pointer-events-none ${className}`}
  >
    <svg
      viewBox="0 0 1440 320"
      className="w-full h-full absolute bottom-0"
      preserveAspectRatio="none"
      role="presentation"
      aria-hidden="true"
    >
      <path
        d="M0,160 C150,210 350,110 500,160 S850,210 1000,160 S1250,110 1440,160 V320 H0 Z"
        fill="currentColor"
      />
    </svg>
  </div>
);

// --- COMPONENT IMPLEMENTATION --- //
export const NutritionHeroHeader: React.FC<NutritionHeroHeaderProps> = ({
  title = 'Pure Ingredients. Zero Secrets.',
  description = 'Explore the meticulous breakdown of every confection we craft. From macro-nutrients to premium artisan ingredients, we believe absolute perfection requires absolute transparency.',
  className = '',
  isCurved = true,
  children,
}) => {
  // Framer Motion Orchestration (Perfectly aligned with HomeHeader & DeliveryHeroHeader)
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.05 },
    },
  } as const;

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: { type: 'spring', stiffness: 70, damping: 15 },
    },
  } as const;

  return (
    <section
      className={`relative w-full !bg-brand-sage min-h-[50vh] md:min-h-[60vh] flex flex-col justify-center overflow-hidden ${className} ${
        isCurved ? 'pb-32 sm:pb-40' : 'pb-16 border-b border-brand-frost'
      }`}
    >
      <div className="relative z-10 container max-w-screen-xl mx-auto px-6 pt-12 md:pt-16 md:px-10 flex flex-col md:flex-row items-center gap-12 lg:gap-16">
        {/* ============================================================================ */}
        {/* LEFT COLUMN: Typography & Content (DNA matched to DeliveryHeroHeader)        */}
        {/* ============================================================================ */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="show"
          className="flex-1 w-full text-center md:text-left flex flex-col justify-center max-w-xl mx-auto md:mx-0"
        >
          {/* Eyebrow with Lucide Icon */}
          <motion.div
            variants={itemVariants}
            className="flex items-center justify-center md:justify-start gap-2 mb-3 !text-brand-frost-wash"
          >
            <Leaf size={16} strokeWidth={2.5} className="drop-shadow-sm" />
            <p className="font-display font-bold text-xs sm:text-sm tracking-[0.2em] uppercase">
              Nutritional Profile
            </p>
          </motion.div>

          <motion.h1
            variants={itemVariants}
            className="font-display font-[700] text-3xl sm:text-4xl lg:text-5xl !text-brand-frost-wash !tracking-wide leading-[1.1]"
          >
            {title}
          </motion.h1>

          <motion.p
            variants={itemVariants}
            className="mt-4 font-body text-brand-frost-wash font-[400] text-base sm:text-lg leading-relaxed"
          >
            {description}
          </motion.p>

          {/* Optional Data Injection (e.g., search bars, category filters) */}
          {children && (
            <motion.div
              variants={itemVariants}
              className="mt-8 w-full text-left"
            >
              {children}
            </motion.div>
          )}
        </motion.div>

        {/* ============================================================================ */}
        {/* RIGHT COLUMN: Premium Visual Entrance                                        */}
        {/* ============================================================================ */}
        <motion.div
          initial={{ opacity: 0, x: 30, scale: 0.95 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
          className="flex-1 w-full max-w-sm md:max-w-md lg:max-w-lg flex justify-center items-center relative"
        >
          {/* Ambient Glow behind the image to make it "pop" */}
          <div className="absolute inset-0 bg-brand-sage/10 blur-[80px] rounded-full scale-125 z-0 pointer-events-none" />

          <motion.img
            src={NutritionVisual}
            alt="Fresh artisanal ingredients"
            className="w-full h-auto object-contain relative z-10 drop-shadow-[0_20px_30px_rgba(0,0,0,0.15)] will-change-transform"
            // Subtle floating effect on hover
            whileHover={{ y: -8 }}
            transition={{ type: 'spring', stiffness: 200, damping: 20 }}
            loading="eager" // Eager loading since it's above the fold
          />
        </motion.div>
      </div>

      {/* Aligned CurveDivider to prevent background bleeding */}
      {isCurved && (
        <CurveDivider className="!text-white translate-y-[1px] z-30" />
      )}
    </section>
  );
};

export default NutritionHeroHeader;
